import { useContext, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import EchoContext from 'context/EchoContext';
import { createSnack } from 'common/utils/general';
import uiSlice from 'store/modules/ui';

/**
 * Hook to listen for news events via WebSocket (public channel).
 * Shows an in-app snackbar to every connected user when an article is published or updated.
 */
function useNewsListener() {
  const { t } = useTranslation(['notifications']);
  const dispatch = useDispatch();
  const echoContext = useContext(EchoContext);
  const echo = echoContext?.echo || null;
  const channelRef = useRef(null);

  useEffect(() => {
    if (!echo || typeof echo.channel !== 'function') {
      return undefined;
    }

    if (channelRef.current) return undefined;

    let channel;
    try {
      channel = echo.channel('news');
      channelRef.current = channel;
    } catch (error) {
      return undefined;
    }

    // Publicación y actualización comparten aviso: cambia el texto, no el comportamiento.
    const announce = (messageKey, fallback) => (eventData) => {
      const snack = createSnack({
        key: `news-${messageKey}-${Date.now()}`,
        variant: 'info',
        message: t(messageKey, { title: eventData?.title ?? '', ns: 'notifications', defaultValue: fallback }),
        options: { persist: false, autoHideDuration: 6000 }
      });
      dispatch(uiSlice.actions.enqueueSnackbar(snack));
    };

    channel.listen('.news.created', announce('news.published', 'Nueva noticia publicada: {{title}}'));
    channel.listen('.news.updated', announce('news.updatedToast', 'Se actualizó una noticia: {{title}}'));

    return () => {
      if (channelRef.current && echo && typeof echo.leave === 'function') {
        try {
          channelRef.current.stopListening('.news.created');
          channelRef.current.stopListening('.news.updated');
          echo.leave('news');
          channelRef.current = null;
        } catch (_) {
          // silent cleanup
        }
      }
    };
  }, [echo, dispatch, t]);
}

export default useNewsListener;
